import { isDueToday, isOverdue } from "./taskDates";

export { isDueToday, isOverdue };

function isCompletedToday(task) {
    if (String(task.status || "") !== "Completed") return false;
    const events = Array.isArray(task.historyTasks) ? task.historyTasks : [];
    const done = [...events].reverse().find((h) => String(h.title || "") === "Completed");
    if (!done?.createdAt) return false;
    const d = new Date(done.createdAt);
    if (Number.isNaN(d.getTime())) return false;
    const today = new Date();
    return (
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate()
    );
}

function isClosed(task) {
    const s = String(task?.status || "").toLowerCase();
    return s === "completed" || s === "cancelled";
}

function isRecent(task, days = 7) {
    const created = new Date(task.createdAt || task.created_at || task.updatedAt);
    if (Number.isNaN(created.getTime())) return false;
    return Date.now() - created.getTime() <= days * 24 * 60 * 60 * 1000;
}

export function computeTaskStats(tasks) {
    const active = tasks.filter((t) => !isClosed(t));
    const completed = tasks.filter((t) => t.status === "Completed");
    const open = active.filter((t) => String(t.status || "Open") === "Open" && !t.paused);
    const inProgress = active.filter((t) => t.status === "In Progress" && !t.paused);
    const dueToday = active.filter(isDueToday);
    const overdue = active.filter(isOverdue);
    const highPriority = active.filter((t) => t.priority?.toLowerCase() === "high");
    const critical = active.filter((t) => t.priority?.toLowerCase() === "critical");
    const paused = active.filter((t) => t.paused);
    const completedToday = tasks.filter(isCompletedToday);
    const assignees = new Set(active.map((t) => String(t.assign_to ?? "unassigned")));
    const recentlyAssigned = active.filter(isRecent);

    return {
        total: tasks.length,
        active: active.length,
        pending: active.length,
        recentlyAssigned: recentlyAssigned.length,
        open: open.length,
        inProgress: inProgress.length,
        completed: completed.length,
        completedToday: completedToday.length,
        dueToday: dueToday.length,
        overdue: overdue.length,
        highPriority: highPriority.length,
        critical: critical.length,
        paused: paused.length,
        workloadPeople: assignees.size,
        workload: assignees.size ? `${active.length}/${assignees.size}` : "0",
    };
}

export function groupTasksByAssignee(tasks, assignOptions = []) {
    const groups = new Map();

    const labelFor = (assignTo) => {
        const opt = assignOptions.find(
            (o) => String(o.value) === String(assignTo)
        );
        if (opt) return opt.label;
        return assignTo ? `Staff #${assignTo}` : "Unassigned";
    };

    tasks.forEach((task) => {
        const key = task.assign_to ?? "unassigned";
        if (!groups.has(key)) {
            const staff = task.staffuser;
            const name = staff
                ? `${staff.lastName || ""} ${staff.firstName || ""}`.trim()
                : labelFor(key);
            groups.set(key, {
                assignTo: key,
                name: name || labelFor(key),
                tasks: [],
                active: 0,
                overdue: 0,
                high: 0,
            });
        }
        const g = groups.get(key);
        g.tasks.push(task);
        if (!isClosed(task)) {
            g.active += 1;
            if (isOverdue(task)) g.overdue += 1;
            if (task.priority?.toLowerCase() === "high" || task.priority?.toLowerCase() === "critical") g.high += 1;
        }
    });

    return [...groups.values()].sort((a, b) => b.active - a.active);
}

export function statusBreakdown(tasks) {
    const counts = { Completed: 0, "In Progress": 0, Open: 0, Paused: 0, Blocked: 0, Cancelled: 0 };
    tasks.forEach((t) => {
        if (t.status === "Blocked") {
            counts.Blocked = (counts.Blocked || 0) + 1;
        } else if (t.status === "Cancelled") {
            counts.Cancelled += 1;
        } else if (t.paused && t.status !== "Completed") {
            counts.Paused += 1;
        } else if (t.status === "Completed") {
            counts.Completed += 1;
        } else if (t.status === "In Progress") {
            counts["In Progress"] += 1;
        } else {
            counts.Open += 1;
        }
    });
    return Object.entries(counts)
        .filter(([, v]) => v > 0)
        .map(([label, value]) => ({ label, value }));
}
